/* ============================================================================
 * Reusable POP Message system
 * - Single global modal renderer + imperative API: popMessage.show({...})
 * - Severity levels: info | success | warning | sensitive | slow
 * - Use anywhere: import { popMessage } from "@/lib/popMessage"; popMessage.show({...})
 * ========================================================================== */
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
    Info, CheckCircle2, AlertTriangle, ShieldAlert, Hourglass, X, PartyPopper,
} from "lucide-react";

const ICONS = {
    info: Info,
    success: CheckCircle2,
    warning: AlertTriangle,
    sensitive: ShieldAlert,
    slow: Hourglass,
    celebrate: PartyPopper,
};

const STYLES = {
    info:      { iconBg: "bg-[#E3F2FD]", iconFg: "text-[#1565C0]", accent: "border-[#1565C0]" },
    success:   { iconBg: "bg-[#E8F5E9]", iconFg: "text-[#1B5E20]", accent: "border-[#1B5E20]" },
    warning:   { iconBg: "bg-[#FFF3E0]", iconFg: "text-[#F57C00]", accent: "border-[#F57C00]" },
    sensitive: { iconBg: "bg-[#FFEBEE]", iconFg: "text-[#C62828]", accent: "border-[#C62828]" },
    slow:      { iconBg: "bg-[#FFF8E1]", iconFg: "text-[#B08300]", accent: "border-[#B08300]" },
    celebrate: { iconBg: "bg-[#FFF3E0]", iconFg: "text-[#F57C00]", accent: "border-[#F57C00]" },
};

let _listeners = new Set();

export const popMessage = {
    show(opts) {
        _listeners.forEach((fn) => fn({ ...opts, _id: Date.now() + Math.random() }));
    },
};

export function PopMessageProvider() {
    const [msg, setMsg] = useState(null);

    useEffect(() => {
        const onShow = (m) => setMsg(m);
        _listeners.add(onShow);
        return () => { _listeners.delete(onShow); };
    }, []);

    if (!msg) return null;

    const severity = msg.severity || "info";
    const Icon = ICONS[severity] || Info;
    const style = STYLES[severity] || STYLES.info;
    const dismissLabel = msg.dismissLabel || "Got it";

    const onDismiss = () => {
        setMsg(null);
        msg.onDismiss?.();
    };
    const onConfirm = () => {
        setMsg(null);
        msg.onConfirm?.();
    };

    return createPortal(
        <div
            data-testid={`pop-message-${severity}`}
            className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
            onClick={msg.dismissOnBackdrop !== false ? onDismiss : undefined}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className={`relative w-full max-w-md bg-white rounded-2xl shadow-2xl border-l-4 ${style.accent} animate-in slide-in-from-bottom-4 fade-in duration-200 overflow-hidden`}
            >
                {severity === "celebrate" && (
                    <div className="pointer-events-none absolute inset-0" aria-hidden="true" data-testid="pop-message-confetti">
                        {Array.from({ length: 14 }).map((_, i) => (
                            <span
                                key={i}
                                className="vt-confetti"
                                style={{
                                    left: `${6 + i * 6.5}%`,
                                    background: i % 3 === 0 ? "#F57C00" : i % 3 === 1 ? "#1B5E20" : "#C9A55B",
                                    animationDelay: `${(i % 7) * 0.18}s`,
                                }}
                            />
                        ))}
                    </div>
                )}
                <button
                    type="button"
                    onClick={onDismiss}
                    data-testid="pop-message-close"
                    className="absolute top-3 right-3 h-8 w-8 inline-flex items-center justify-center rounded-full text-[#666] hover:bg-[#F5F5F5]"
                    aria-label="Close"
                >
                    <X className="h-4 w-4" />
                </button>
                <div className="p-6 sm:p-7">
                    <div className={`inline-flex h-11 w-11 items-center justify-center rounded-xl ${style.iconBg}`}>
                        <Icon className={`h-5 w-5 ${style.iconFg}`} />
                    </div>
                    <h3
                        data-testid="pop-message-title"
                        className="mt-4 text-[18px] font-bold text-[#0F2B12] leading-snug pr-6"
                    >
                        {msg.title}
                    </h3>
                    {msg.body && (
                        <p
                            data-testid="pop-message-body"
                            className="mt-2 text-[14px] text-[#555] leading-6"
                        >
                            {msg.body}
                        </p>
                    )}
                    {msg.bullets && msg.bullets.length > 0 && (
                        <ul className="mt-3 space-y-1.5 text-[13.5px] text-[#444]">
                            {msg.bullets.map((b, i) => (
                                <li key={i} className="flex gap-2">
                                    <span className={`mt-1 inline-block h-1.5 w-1.5 rounded-full ${style.iconBg} ring-2 ${style.accent}`} />
                                    <span>{b}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                    <div className="mt-6 flex flex-col-reverse sm:flex-row gap-2.5">
                        {msg.onConfirm && (
                            <button
                                type="button"
                                onClick={onDismiss}
                                data-testid="pop-message-cancel"
                                className="flex-1 h-11 rounded-xl border border-[#E0E0E0] text-[14px] font-semibold text-[#444] hover:bg-[#F5F5F5]"
                            >
                                {msg.cancelLabel || "Cancel"}
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={msg.onConfirm ? onConfirm : onDismiss}
                            data-testid="pop-message-confirm"
                            className={`flex-1 h-11 rounded-xl text-[14px] font-semibold text-white shadow-sm ${msg.destructive ? "bg-[#C62828] hover:bg-[#8E0000]" : "bg-[#1B5E20] hover:bg-[#0F3311]"}`}
                        >
                            {msg.confirmLabel || dismissLabel}
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body,
    );
}

/* Promise-based confirmation — `if (!(await popConfirm({...}))) return;` */
export const popConfirm = ({ title, body, confirmLabel = "Yes, continue", cancelLabel = "Cancel", severity = "warning", destructive = false } = {}) =>
    new Promise((resolve) => {
        popMessage.show({
            title, body, severity, destructive, confirmLabel, cancelLabel,
            dismissOnBackdrop: true,
            onConfirm: () => resolve(true),
            onDismiss: () => resolve(false),
        });
    });

/* Celebratory popup with confetti */
export const popCelebrate = (opts) => popMessage.show({ dismissLabel: "Continue", ...opts, severity: "celebrate" });

/* Convenience helpers */
export const popInfo      = (opts) => popMessage.show({ ...opts, severity: "info" });
export const popSuccess   = (opts) => popMessage.show({ ...opts, severity: "success" });
export const popWarning   = (opts) => popMessage.show({ ...opts, severity: "warning" });
export const popSensitive = (opts) => popMessage.show({ ...opts, severity: "sensitive" });
export const popSlow      = (opts) => popMessage.show({ ...opts, severity: "slow" });
